import React from 'react'

import * as common from '../../../common'

import * as Mui from '@mui/material'

import Pad from '../Pad'

import styles from './SignatureButton.module.css'
import { RoofClaimContext } from './RoofClaimContext'

interface SignatureButtonProps {
  customerID: string
  field: keyof common.Customer
  handleOpen: () => void
}

export default React.memo<SignatureButtonProps>(function ({
  customerID,
  field,
  handleOpen,
}) {
  const { readOnly, isPDF } = React.useContext(RoofClaimContext)
  const selectCustomerField = React.useMemo(
    (): ReturnType<typeof common.makeSelectCustomerField> =>
      common.makeSelectCustomerField(),
    [],
  )
  const selectCustomerFieldArgs = React.useMemo(
    (): common.SelectCustomerFieldParams => ({
      customerID,
      field,
    }),
    [customerID, field],
  )

  const selectCustomerCompanyArgs = React.useMemo(
    (): common.SelectCustomerFieldParams => ({
      customerID,
      field: 'solarCompany',
    }),
    [customerID],
  )

  const solarCompany = common.useSelector(
    (_): common.ValueOf<common.Customer> =>
      selectCustomerField(_, selectCustomerCompanyArgs),
  )

  // Remember to subscribe to the customer elsewhere!
  const currentValue = common.useSelector(
    (_): common.ValueOf<common.Customer> =>
      selectCustomerField(_, selectCustomerFieldArgs),
  )

  const dateValue = common.useSelector(
    (state): common.ValueOf<common.Customer> =>
      selectCustomerField(state, {
        customerID,
        field: (field + '_date') as keyof common.Customer,
      }),
  )
  const buttonStyles = React.useMemo(
    () => ({
      backgroundColor: common.companyColors[solarCompany as string],
      width: '203px',
      '&:hover': {
        backgroundColor: common.companyColors[solarCompany as string],
      },
    }),
    [solarCompany],
  )
  return (
    <div className={styles['signature-button']}>
      {currentValue ? (
        <img src={currentValue as string} alt="Signature" />
      ) : null}
      <hr />
      <Pad amt={10} />

      <p className={styles['dateLabel']}>Date</p>
      <Pad amt={5} />
      <p style={{ minHeight: 24, width: 203, fontSize: 12 }}>
        {dateValue
          ? common.formatContractDate(
              common.normalizeTimestampToMs(Number(dateValue)),
              String(solarCompany),
              'MM/DD/YYYY - hh:mma',
            )
          : ''}
      </p>

      <Pad amt={20} />

      {!isPDF && (
        <Mui.Button
          data-guided-signature={field}
          disabled={
            !!currentValue ||
            (readOnly && field === 'roof_claiming_company_rep_sign')
          }
          onClick={() => {
            if (
              !currentValue &&
              !(readOnly && field === 'roof_claiming_company_rep_sign')
            )
              handleOpen()
          }}
          size="small"
          sx={buttonStyles}
          variant="contained"
        >
          {field === 'roof_claiming_company_rep_sign'
            ? 'Authorized Signature'
            : 'Client(s) Signature'}
        </Mui.Button>
      )}
      {isPDF && <p>{common.fieldToLabel[field]}</p>}
    </div>
  )
})
